import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import type { PromotionType } from '@/lib/marketing';
import i18n from '@/i18n';

type MarketingCampaign = components['schemas']['MarketingCampaignResource'];
type MarketingApplication = components['schemas']['MarketingApplicationResource'];
type MerchantCashbackOffer = components['schemas']['MerchantCashbackOfferResource'];
type MarketingCampaignItem = components['schemas']['MarketingCampaignItemResource'];
type ActiveMarketingItem = components['schemas']['ActiveMarketingItemResource'];

function invalidateMarketing(qc: QueryClient) {
  qc.invalidateQueries({
    predicate: (q) => {
      const key = q.queryKey[0];
      return typeof key === 'string' && (key.startsWith('/marketing') || key.startsWith('/admin/marketing'));
    },
  });
}

// ---------- Reads ----------

export function useMarketingCampaigns(filters?: { promotion_type?: PromotionType | 'all'; status?: string }) {
  return useQuery({
    queryKey: ['/admin/marketing/campaigns', {
      params: {
        query: {
          promotion_type: filters?.promotion_type && filters.promotion_type !== 'all' ? filters.promotion_type : undefined,
          status: filters?.status && filters.status !== 'all' ? filters.status : undefined,
        } as never,
      },
    }],
    select: (res: { data?: MarketingCampaign[] }) => res?.data ?? [],
  });
}

export function useMarketingCampaign(id?: string) {
  return useQuery({
    queryKey: ['/admin/marketing/campaigns/{campaign}', { params: { path: { campaign: Number(id) } } }],
    enabled: !!id,
    select: (res: { data?: MarketingCampaign }) => res?.data,
  });
}

export function useCampaignItems(campaignId?: string) {
  return useQuery({
    queryKey: ['/admin/marketing/campaigns/{campaign}', { params: { path: { campaign: Number(campaignId) } } }],
    enabled: !!campaignId,
    select: (res: { data?: MarketingCampaign }) => res?.data?.items ?? [],
  });
}

type ApplicationFilters = { status?: string; promotion_type?: string; search?: string; page?: number; per_page?: number };

function adminApplicationsKey(opts?: ApplicationFilters) {
  return ['/admin/marketing/applications', {
    params: {
      query: {
        status: opts?.status && opts.status !== 'all' ? opts.status : undefined,
        promotion_type: opts?.promotion_type && opts.promotion_type !== 'all' ? opts.promotion_type : undefined,
        search: opts?.search?.trim() || undefined,
        page: opts?.page,
        per_page: opts?.per_page,
      } as never,
    },
  }] as const;
}

export function useMarketingApplications(opts?: { adminView?: boolean } & ApplicationFilters) {
  const admin = !!opts?.adminView;
  return useQuery({
    queryKey: admin ? adminApplicationsKey(opts) : ['/marketing/applications'],
    select: (res: { data?: MarketingApplication[] }) => res?.data ?? [],
  });
}

/** FR-97: admin applications page by page (server-side search by merchant name). */
export function useAdminMarketingApplicationsPage(opts: ApplicationFilters) {
  return useQuery({
    queryKey: adminApplicationsKey(opts),
    placeholderData: (prev) => prev,
    select: (res: { data?: MarketingApplication[]; meta?: { current_page: number; last_page: number; per_page: number; total: number } }) => ({
      items: res?.data ?? [],
      meta: res?.meta ?? { current_page: 1, last_page: 1, per_page: opts.per_page ?? 50, total: res?.data?.length ?? 0 },
    }),
  });
}

export function useCashbackOffers(opts?: { adminView?: boolean }) {
  const admin = !!opts?.adminView;
  return useQuery({
    queryKey: [admin ? '/admin/marketing/cashback-offers' : '/marketing/cashback-offers'],
    select: (res: { data?: MerchantCashbackOffer[] }) => res?.data ?? [],
  });
}

/** Merchant's active promotions (campaign items where the merchant participates). */
export function useMerchantPromotions() {
  return useQuery({
    queryKey: ['/marketing/my-promotions'],
    select: (res: { data?: MarketingCampaignItem[] }) => res?.data ?? [],
  });
}

/** HisobAPP Home output: only currently active items. */
export function useActiveMarketingItems(promotionType?: PromotionType) {
  return useQuery({
    queryKey: ['/marketing/active-items', { params: { query: (promotionType ? { promotion_type: promotionType } : {}) as never } }],
    select: (res: { data?: ActiveMarketingItem[] }) => res?.data ?? [],
  });
}

export function useMarketingAudit(entityType?: string, entityId?: string, opts?: { adminView?: boolean; campaignId?: string }) {
  const admin = !!opts?.adminView;
  return useQuery({
    queryKey: [admin ? '/admin/marketing/audit-logs' : '/marketing/audit-logs', {
      params: {
        query: {
          entity_type: entityType,
          entity_id: entityId ? Number(entityId) : undefined,
          campaign_id: opts?.campaignId ? Number(opts.campaignId) : undefined,
        } as never,
      },
    }],
    select: (res: { data?: components['schemas']['MarketingAuditLogResource'][] }) => res?.data ?? [],
  });
}

/**
 * Global categories a merchant may promote (FR-97): its own for merchant staff,
 * the chosen merchant's for a platform admin (`merchantId` required there).
 */
export function useEligibleCategories(merchantId?: string, opts?: { adminView?: boolean }) {
  const admin = !!opts?.adminView;
  return useQuery({
    queryKey: ['/marketing/eligible-categories', { params: { query: (merchantId ? { merchant_id: Number(merchantId) } : {}) as never } }],
    enabled: !admin || !!merchantId,
    select: (res: { data?: { id: number; name: string }[] }) => res?.data ?? [],
  });
}

export function useMarketingMerchants() {
  return useQuery({
    queryKey: ['/admin/merchants'],
    select: (res: { data?: { id: number; name: string }[] }) => res?.data ?? [],
  });
}

// ---------- Mutations ----------

type Payload = Record<string, unknown>;

async function runMarketingAction(action: string, payload: Payload) {
  switch (action) {
    case 'start_review':
      return unwrap(apiClient.PATCH('/admin/marketing/applications/{application}/status', {
        params: { path: { application: Number(payload.id) } },
        body: { to_status: 'UNDER_REVIEW' },
      }));
    case 'approve_application':
      return unwrap(apiClient.PATCH('/admin/marketing/applications/{application}/status', {
        params: { path: { application: Number(payload.id) } },
        body: { to_status: 'APPROVED', campaign_id: payload.campaign_id ? Number(payload.campaign_id) : undefined } as never,
      }));
    case 'reject_application':
      return unwrap(apiClient.PATCH('/admin/marketing/applications/{application}/status', {
        params: { path: { application: Number(payload.id) } },
        body: { to_status: 'REJECTED', reason: String(payload.rejection_reason ?? '') },
      }));
    case 'request_changes':
      return unwrap(apiClient.PATCH('/admin/marketing/applications/{application}/status', {
        params: { path: { application: Number(payload.id) } },
        body: { to_status: 'DRAFT', comment: String(payload.admin_comment ?? '') },
      }));
    case 'add_internal_comment':
      return unwrap(apiClient.PATCH('/admin/marketing/applications/{application}/comment', {
        params: { path: { application: Number(payload.id) } },
        body: { admin_comment: String(payload.admin_comment ?? '') },
      }));
    case 'set_campaign_status':
      return unwrap(apiClient.PATCH('/admin/marketing/campaigns/{campaign}/status', {
        params: { path: { campaign: Number(payload.id) } },
        body: { to_status: payload.status as never },
      }));
    case 'add_campaign_item':
      return unwrap(apiClient.POST('/admin/marketing/campaigns/{campaign}/items', {
        params: { path: { campaign: Number(payload.campaign_id) } },
        body: {
          merchant_id: Number(payload.merchant_id),
          category_id: payload.category_id ? Number(payload.category_id) : undefined,
          promo_title: payload.promo_title as string | undefined,
          promo_subtitle: payload.promo_subtitle as string | undefined,
          promo_image_url: payload.promo_image_url as string | undefined,
          // BIG_CASHBACK: the backend auto-picks the merchant's approved offer when omitted.
          cashback_offer_id: payload.cashback_offer_id ? Number(payload.cashback_offer_id) : undefined,
          application_id: payload.application_id ? Number(payload.application_id) : undefined,
          display_order: payload.display_order != null ? Number(payload.display_order) : undefined,
          start_at: (payload.start_at as string | undefined) || undefined,
          end_at: (payload.end_at as string | undefined) || undefined,
        } as never,
      }));
    case 'update_campaign_item':
      return unwrap(apiClient.PATCH('/admin/marketing/campaign-items/{item}', {
        params: { path: { item: Number(payload.id) } },
        body: (payload.updates ?? {}) as never,
      }));
    case 'remove_campaign_item':
      return unwrap(apiClient.DELETE('/admin/marketing/campaign-items/{item}', {
        params: { path: { item: Number(payload.id) } },
      }));
    case 'reorder_campaign_items':
      return unwrap(apiClient.PATCH('/admin/marketing/campaigns/{campaign}/items/reorder', {
        params: { path: { campaign: Number(payload.campaign_id) } },
        body: { order: payload.order as never },
      }));
    case 'duplicate_campaign':
      return unwrap(apiClient.POST('/admin/marketing/campaigns/{campaign}/duplicate', {
        params: { path: { campaign: Number(payload.id) } },
      }));
    case 'create_campaign':
      return unwrap(apiClient.POST('/admin/marketing/campaigns', { body: payload as never }));
    case 'approve_cashback_offer':
      return unwrap(apiClient.PATCH('/admin/marketing/cashback-offers/{offer}/status', {
        params: { path: { offer: Number(payload.id) } },
        body: { to_status: 'APPROVED' },
      }));
    case 'reject_cashback_offer':
      return unwrap(apiClient.PATCH('/admin/marketing/cashback-offers/{offer}/status', {
        params: { path: { offer: Number(payload.id) } },
        body: { to_status: 'REJECTED' },
      }));
    case 'submit_application':
      return unwrap(apiClient.POST('/marketing/applications', { body: { ...payload, submit: true } as never }));
    case 'withdraw_application':
      return unwrap(apiClient.POST('/marketing/applications/{application}/withdraw', {
        params: { path: { application: Number(payload.id) } },
      }));
    case 'request_cancellation':
      return unwrap(apiClient.POST('/marketing/campaign-items/{item}/request-cancellation', {
        params: { path: { item: Number(payload.item_id) } },
        body: { reason: payload.reason as string | undefined },
      }));
    default:
      throw new Error(`Unknown marketing action: ${action}`);
  }
}

async function unwrap<T>(promise: Promise<{ data?: T; error?: unknown }>): Promise<T | undefined> {
  const { data, error } = await promise;
  if (error) {
    const message = typeof error === 'object' && error !== null && 'message' in error
      ? String((error as { message: unknown }).message)
      : i18n.t('misc_error.title');
    throw new Error(message);
  }
  return data;
}

export function useMarketingMutation(action: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Payload) => runMarketingAction(action, payload),
    onSuccess: () => invalidateMarketing(qc),
  });
}
